<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Check, Clock, Download, ExternalLink, Loader2 } from 'lucide-svelte';

  import {
    downloadExport,
    fetchExportHistory,
    ExportRefused,
    type PastExport
  } from '$lib/api/accountorder';
  import { notifications } from '$lib/stores/notifications';

  /**
   * What this organisation has exported.
   *
   * # The organisation's, not the caller's
   *
   * A seat shares the subscription, the invoice address and the terms, so an
   * export placed by a colleague is the organisation's spending. Showing each
   * person only their own would hide it from the people paying for it.
   *
   * # The figures are read, never recomputed
   *
   * Net, VAT and gross come from the order, where they were stored as the
   * customer was quoted them. Working them out again from the record count and
   * the current tiers would be a second calculation — and the one thing a
   * history must not do is disagree with the invoice already in somebody's
   * inbox. Orders placed before the amounts were recorded say so rather than
   * showing CHF 0.00.
   */

  let orders = $state<PastExport[]>([]);
  let loading = $state(true);
  let refusal = $state('');

  $effect(() => {
    void fetchExportHistory()
      .then((found) => {
        orders = found;
      })
      .catch((error) => {
        refusal = error instanceof ExportRefused ? error.message : $_('export.failed');
      })
      .finally(() => {
        loading = false;
      });
  });

  /** Which order is being fetched, so the spinner sits on the right row. */
  let downloading = $state(0);

  async function take(order: PastExport) {
    downloading = order.order_id;
    try {
      await downloadExport(order.order_id, order.invoice_number || `export-${order.order_id}`);
    } catch (error) {
      notifications.error(
        error instanceof ExportRefused ? error.message : $_('export.history.downloadFailed')
      );
    } finally {
      downloading = 0;
    }
  }

  const money = (order: PastExport) =>
    `${order.currency} ${order.gross.toLocaleString('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const when = (iso: string) => {
    const at = new Date(iso);
    return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('de-CH');
  };
</script>

<div class="flex h-full flex-col items-center overflow-y-auto px-4 py-10 sm:px-6">
  <div class="w-full max-w-3xl">
    <h1 class="mb-1 text-2xl font-semibold text-gray-900">{$_('export.history.title')}</h1>
    <p class="mb-8 text-sm text-gray-600">{$_('export.history.subtitle')}</p>

    {#if loading}
      <p class="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 class="h-4 w-4 animate-spin" />
        {$_('export.history.loading')}
      </p>
    {:else if refusal}
      <p class="text-sm text-red-600">{refusal}</p>
    {:else if orders.length === 0}
      <div
        class="rounded-surface border border-dashed border-hairline bg-white px-6 py-12 text-center"
      >
        <p class="text-sm font-medium text-gray-900">{$_('export.history.emptyTitle')}</p>
        <p class="mt-1 text-sm text-gray-500">{$_('export.history.emptyBody')}</p>
      </div>
    {:else}
      <ul
        class="divide-y divide-hairline overflow-hidden rounded-surface border border-hairline bg-white"
      >
        {#each orders as order (order.order_id)}
          <li class="flex flex-wrap items-center gap-3 px-4 py-3">
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-surface {order.delivered_at
                ? 'bg-accent-50 text-accent-600'
                : 'bg-surface-sunken text-gray-500'}"
            >
              {#if order.delivered_at}
                <Check class="h-4 w-4" />
              {:else}
                <Clock class="h-4 w-4" />
              {/if}
            </span>

            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900">
                {$_('export.rows', { values: { count: order.rows } })}
                <span class="font-normal text-gray-500"> · {when(order.placed_at)}</span>
              </p>
              <p class="mt-0.5 text-xs text-gray-500">
                {#if order.delivered_at}
                  {$_('export.history.delivered', { values: { date: when(order.delivered_at) } })}
                {:else if order.settled}
                  {$_('export.history.pendingDelivery')}
                {:else}
                  {$_('export.history.awaitingPayment')}
                {/if}
                {#if order.invoice_number}
                  · {order.invoice_number}
                {/if}
              </p>
            </div>

            <div class="shrink-0 text-right">
              {#if order.priced}
                <p class="text-sm font-medium tabular-nums text-gray-900">{money(order)}</p>
                {#if order.discount_percent > 0}
                  <p class="text-xs text-gray-500">
                    {$_('export.history.discount', {
                      values: { percent: order.discount_percent }
                    })}
                  </p>
                {/if}
              {:else}
                <!-- Placed before the amount was recorded on the order. Not a
                     free export, and saying so beats printing CHF 0.00. -->
                <p class="text-xs text-gray-400">{$_('export.history.noAmount')}</p>
              {/if}
            </div>

            <!--
              The way to pay, while there is one. Only an unpaid prepaid order
              has a link; a postpaid one is invoiced afterwards and a paid one
              has nothing left to do.
            -->
            {#if order.settled}
              <!--
                Free and repeatable. The export was paid for when it was
                placed; this reassembles the same rows from the corpus as it
                stands now, which is what makes a past export worth returning
                to rather than a file to keep somewhere.
              -->
              <button
                type="button"
                onclick={() => take(order)}
                disabled={downloading === order.order_id}
                class="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-hairline px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                {#if downloading === order.order_id}
                  <Loader2 class="h-3.5 w-3.5 animate-spin" />
                {:else}
                  <Download class="h-3.5 w-3.5" />
                {/if}
                {$_('export.history.download')}
              </button>
            {:else if order.payment_url}
              <a
                href={order.payment_url}
                target="_blank"
                rel="noreferrer noopener"
                class="inline-flex shrink-0 items-center gap-1 rounded-control bg-accent-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-accent-700"
              >
                {$_('export.history.pay')}
                <ExternalLink class="h-3 w-3" />
              </a>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</div>
